import React, { useEffect, useRef, useState } from "react";
import { QrCode, Upload, Trash2, Plus, Eye, EyeOff, DollarSign, Megaphone, Link as LinkIcon, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * QrOverlayPanel — host-side control for pinning multiple QR-code images
 * on the live stream. Each slot can be a payment QR (Cash App, Venmo,
 * Zelle), an advertisement QR, an affiliate link QR, or any link. The
 * host can also globally toggle ALL QRs on or off without losing them.
 *
 * State lives on `db.live_streams.overlays`:
 *   - overlays.qr_slots:  [{id, image_url, label, kind, position, enabled}]
 *   - overlays.qr_enabled: boolean (master switch — show/hide all)
 *   - overlays.qr:         legacy single-slot mirror (kept in sync server-side)
 *
 * Up to 4 slots. Each slot ≤ 800 KB.
 */

const KIND_META = {
  payment:   { label: "Payment",   icon: DollarSign,
               activeCls: "bg-emerald-500/25 text-emerald-100 border-emerald-500/50" },
  ad:        { label: "Ad",        icon: Megaphone,
               activeCls: "bg-amber-500/25 text-amber-100 border-amber-500/50" },
  affiliate: { label: "Affiliate", icon: ShoppingBag,
               activeCls: "bg-violet-500/25 text-violet-100 border-violet-500/50" },
  link:      { label: "Link",      icon: LinkIcon,
               activeCls: "bg-sky-500/25 text-sky-100 border-sky-500/50" },
};
const KIND_ORDER = ["payment", "ad", "affiliate", "link"];
const POSITIONS = [
  { id: "bottom-left", label: "BL" },
  { id: "bottom-center", label: "BC" },
  { id: "bottom-right", label: "BR" },
];
const MAX_SLOTS = 4;

const newSlot = (kind = "payment") => ({
  id: `tmp_${Math.random().toString(36).slice(2, 10)}`,
  image_url: "",
  label: "",
  kind,
  position: "bottom-right",
  enabled: true,
});

export default function QrOverlayPanel({ streamId, currentOverlays }) {
  // currentOverlays === full `overlays` object from the live-stream doc.
  const [slots, setSlots] = useState(() => initSlots(currentOverlays));
  const [enabled, setEnabled] = useState(() => initEnabled(currentOverlays));
  const [busy, setBusy] = useState(false);
  const [activeSlotId, setActiveSlotId] = useState(() => {
    const init = initSlots(currentOverlays);
    return init[0]?.id || null;
  });

  // Re-sync if the parent reloads the live stream and the overlay object
  // changes underneath us (e.g. another studio tab edited the QRs).
  useEffect(() => {
    if (!currentOverlays) return;
    setSlots(initSlots(currentOverlays));
    setEnabled(initEnabled(currentOverlays));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(currentOverlays?.qr_slots || [])]);

  const activeSlot = slots.find((s) => s.id === activeSlotId) || slots[0] || null;

  const updateSlot = (id, patch) => {
    setSlots((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };
  const removeSlot = (id) => {
    setSlots((prev) => {
      const next = prev.filter((s) => s.id !== id);
      if (activeSlotId === id) setActiveSlotId(next[0]?.id || null);
      return next;
    });
  };
  const addSlot = (kind = "payment") => {
    if (slots.length >= MAX_SLOTS) {
      toast.info(`You can pin up to ${MAX_SLOTS} QR codes at once.`);
      return;
    }
    const s = newSlot(kind);
    setSlots((prev) => [...prev, s]);
    setActiveSlotId(s.id);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f || !activeSlot) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Pick an image (PNG / JPG / SVG)");
      return;
    }
    const MAX = 800 * 1024;
    if (f.size > MAX) {
      toast.error(`File too large (${Math.round(f.size / 1024)} KB > 800 KB)`);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => updateSlot(activeSlot.id, { image_url: reader.result });
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const persist = async (nextEnabled = enabled, nextSlots = slots) => {
    setBusy(true);
    try {
      // Drop any empty slots before sending — they'd be discarded anyway.
      const cleaned = nextSlots.filter((s) => s.image_url);
      const r = await api.post(`/live/${streamId}/qr-overlay`, {
        enabled: nextEnabled,
        slots: cleaned,
      });
      // Server may have minted real ids — adopt them so subsequent saves
      // don't duplicate slots.
      if (Array.isArray(r.data?.slots)) {
        setSlots(r.data.slots);
        if (!r.data.slots.find((s) => s.id === activeSlotId)) {
          setActiveSlotId(r.data.slots[0]?.id || null);
        }
      }
      return true;
    } catch (e) { toast.error(formatApiError(e)); return false; }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!slots.some((s) => s.image_url)) { toast.info("Upload at least one QR image first"); return; }
    const ok = await persist(true, slots);
    if (ok) toast.success(enabled ? "QR overlay live on stream" : "QR overlay saved (hidden)");
  };

  const toggleMaster = async () => {
    const next = !enabled;
    setEnabled(next);
    const ok = await persist(next, slots);
    if (ok) toast.success(next ? "QR overlay shown on stream" : "QR overlay hidden");
    else setEnabled(!next); // rollback on failure
  };

  const clearAll = async () => {
    setBusy(true);
    try {
      await api.post(`/live/${streamId}/qr-overlay`, { enabled: false, slots: [] });
      setSlots([]); setActiveSlotId(null); setEnabled(false);
      toast.success("All QR overlays cleared");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const fileRef = useRef(null);

  return (
    <section data-testid="qr-overlay-panel" className="rounded-xl border border-white/10 bg-black/40 p-3 mb-4">
      <header className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <QrCode className="w-4 h-4 text-emerald-300" />
          <h3 className="text-[11px] uppercase tracking-[0.25em] font-black text-white/85">QR Overlays</h3>
          <span className="text-[10px] text-white/40">{slots.length}/{MAX_SLOTS}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="qr-overlay-toggle-visibility"
            onClick={toggleMaster}
            disabled={busy || slots.length === 0}
            aria-pressed={enabled}
            className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full inline-flex items-center gap-1 border transition ${
              enabled
                ? "bg-emerald-500/25 text-emerald-100 border-emerald-500/40"
                : "bg-white/5 text-white/55 border-white/10 hover:bg-white/10"
            } disabled:opacity-40`}
            title={enabled ? "Hide all QRs from stream" : "Show all QRs on stream"}
          >
            {enabled ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            {enabled ? "Visible" : "Hidden"}
          </button>
          {slots.length > 0 && (
            <button
              data-testid="qr-overlay-clear-all"
              onClick={clearAll}
              disabled={busy}
              className="text-white/45 hover:text-rose-300 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider"
            ><Trash2 className="w-3 h-3" /> Clear</button>
          )}
        </div>
      </header>

      {/* Slot tabs */}
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        {slots.map((s, idx) => {
          const meta = KIND_META[s.kind] || KIND_META.payment;
          const Icon = meta.icon;
          const isActive = s.id === activeSlotId;
          return (
            <button
              key={s.id}
              data-testid={`qr-overlay-slot-tab-${idx}`}
              onClick={() => setActiveSlotId(s.id)}
              className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider border inline-flex items-center gap-1.5 transition ${
                isActive
                  ? meta.activeCls
                  : "bg-white/5 text-white/60 border-white/10 hover:bg-white/10"
              }`}
            >
              <Icon className="w-3 h-3" />
              {(s.label || meta.label).slice(0, 14)}
              {!s.enabled && <EyeOff className="w-3 h-3 opacity-50" />}
            </button>
          );
        })}
        {slots.length < MAX_SLOTS && (
          <div className="inline-flex gap-1">
            {KIND_ORDER.map((k) => {
              const meta = KIND_META[k];
              const Icon = meta.icon;
              return (
                <button
                  key={k}
                  data-testid={`qr-overlay-add-${k}`}
                  onClick={() => addSlot(k)}
                  className="px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider border bg-white/5 text-white/55 border-dashed border-white/15 hover:bg-white/10 inline-flex items-center gap-1"
                  title={`Add ${meta.label} QR`}
                >
                  <Plus className="w-3 h-3" /><Icon className="w-3 h-3" /> {meta.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {!activeSlot ? (
        <div className="rounded-lg border border-dashed border-white/15 bg-white/5 p-4 text-[11px] text-white/55 text-center">
          No QR codes pinned. Click a kind above to add one (Payment / Ad / Affiliate / Link).
        </div>
      ) : (
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
          <div className="flex gap-3">
            <div className="w-24 h-24 bg-white/5 border border-white/10 rounded-lg grid place-items-center overflow-hidden shrink-0">
              {activeSlot.image_url ? (
                <img data-testid="qr-overlay-preview-img" src={activeSlot.image_url} alt="QR preview" className="w-full h-full object-contain bg-white" />
              ) : (
                <QrCode className="w-8 h-8 text-white/25" />
              )}
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <button
                data-testid="qr-overlay-upload"
                onClick={() => fileRef.current?.click()}
                className="w-full text-[11px] font-black uppercase tracking-wider px-2 py-1.5 rounded border bg-white/5 text-white/70 border-white/10 hover:bg-white/10 inline-flex items-center justify-center gap-1.5"
              ><Upload className="w-3 h-3" /> {activeSlot.image_url ? "Replace QR" : "Upload QR"}</button>
              <input
                ref={fileRef}
                data-testid="qr-overlay-file-input"
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={onFile}
                className="hidden"
              />
              <input
                data-testid="qr-overlay-label-input"
                value={activeSlot.label}
                onChange={(e) => updateSlot(activeSlot.id, { label: e.target.value.slice(0, 60) })}
                placeholder={`Label (e.g. ${KIND_META[activeSlot.kind]?.label || "Tip me"})`}
                className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-[11px] text-white"
              />
            </div>
          </div>

          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <div className="text-[10px] text-white/55">Kind:</div>
            {KIND_ORDER.map((k) => {
              const meta = KIND_META[k];
              return (
                <button
                  key={k}
                  data-testid={`qr-overlay-kind-${k}`}
                  onClick={() => updateSlot(activeSlot.id, { kind: k })}
                  className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider border transition ${
                    activeSlot.kind === k
                      ? meta.activeCls
                      : "bg-white/5 text-white/55 border-white/10 hover:bg-white/10"
                  }`}
                >{meta.label}</button>
              );
            })}
          </div>

          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <div className="text-[10px] text-white/55">Position:</div>
            {POSITIONS.map((p) => (
              <button
                key={p.id}
                data-testid={`qr-overlay-position-${p.id}`}
                onClick={() => updateSlot(activeSlot.id, { position: p.id })}
                className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider border transition ${
                  activeSlot.position === p.id
                    ? "bg-emerald-500/25 text-emerald-100 border-emerald-500/50"
                    : "bg-white/5 text-white/55 border-white/10 hover:bg-white/10"
                }`}
              >{p.label}</button>
            ))}
            <div className="ml-auto flex items-center gap-1.5">
              <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-black text-white/65 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="qr-overlay-slot-enabled"
                  checked={activeSlot.enabled}
                  onChange={(e) => updateSlot(activeSlot.id, { enabled: e.target.checked })}
                  className="accent-emerald-400"
                />
                Show this QR
              </label>
              <button
                data-testid="qr-overlay-slot-remove"
                onClick={() => removeSlot(activeSlot.id)}
                className="text-white/40 hover:text-rose-300 ml-1"
                title="Remove this slot"
              ><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        </div>
      )}

      <button
        data-testid="qr-overlay-save"
        onClick={save}
        disabled={busy || !slots.some((s) => s.image_url)}
        className="mt-3 w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black font-black text-[11px] uppercase tracking-wider py-2 rounded"
      >{busy ? "Saving…" : enabled ? "Save & show on stream" : "Save (currently hidden)"}</button>
    </section>
  );
}

function initSlots(overlays) {
  if (!overlays) return [];
  const list = Array.isArray(overlays.qr_slots) ? overlays.qr_slots : null;
  if (list && list.length) {
    return list.map((s) => ({
      id: s.id || `tmp_${Math.random().toString(36).slice(2, 10)}`,
      image_url: s.image_url || "",
      label: s.label || "",
      kind: s.kind && KIND_META[s.kind] ? s.kind : "payment",
      position: POSITIONS.some((p) => p.id === s.position) ? s.position : "bottom-right",
      enabled: s.enabled !== false,
    }));
  }
  // Legacy single-slot doc — surface it as the only slot so the host can
  // edit it without losing it.
  if (overlays.qr?.image_url) {
    return [{
      id: `legacy_${Math.random().toString(36).slice(2, 8)}`,
      image_url: overlays.qr.image_url,
      label: overlays.qr.label || "",
      kind: "payment",
      position: overlays.qr.position || "bottom-right",
      enabled: true,
    }];
  }
  return [];
}

function initEnabled(overlays) {
  if (!overlays) return false;
  if (typeof overlays.qr_enabled === "boolean") return overlays.qr_enabled;
  // Legacy: a saved single-slot QR was always shown.
  return !!overlays.qr?.image_url;
}

/**
 * QrOverlayLayer — viewer-side renderer that draws every enabled host QR
 * overlay on top of the live video. Mount inside the player container
 * with `position: absolute; inset: 0`.
 *
 * Props:
 *   - overlays: full overlays object from the live-stream doc (preferred).
 *   - qr:       legacy single-slot QR object (optional fallback).
 *
 * Multiple slots sharing the same position stack horizontally so they
 * don't overlap.
 */
export function QrOverlayLayer({ overlays, qr }) {
  // Prefer multi-slot model when present + master toggle is on.
  let slots = [];
  if (overlays && typeof overlays === "object") {
    const masterEnabled = overlays.qr_enabled !== false; // default: show
    if (Array.isArray(overlays.qr_slots) && overlays.qr_slots.length) {
      if (masterEnabled) slots = overlays.qr_slots.filter((s) => s?.enabled !== false && s?.image_url);
    } else if (overlays.qr?.image_url && masterEnabled) {
      slots = [{ ...overlays.qr, kind: "payment", enabled: true }];
    }
  } else if (qr?.image_url) {
    // Watch.jsx old call signature: <QrOverlayLayer qr={live.overlays?.qr} />
    slots = [{ ...qr, kind: "payment", enabled: true }];
  }

  if (!slots.length) return null;

  // Group by position so we can stagger them horizontally.
  const groups = { "bottom-left": [], "bottom-center": [], "bottom-right": [] };
  slots.forEach((s) => {
    const pos = groups[s.position] ? s.position : "bottom-right";
    groups[pos].push(s);
  });

  return (
    <div data-testid="qr-overlay-layer" className="absolute inset-0 pointer-events-none">
      {Object.entries(groups).map(([pos, items]) => (
        items.length ? (
          <div
            key={pos}
            className={`absolute flex gap-2 ${
              pos === "bottom-left" ? "left-4 bottom-4 flex-row" :
              pos === "bottom-center" ? "left-1/2 -translate-x-1/2 bottom-4 flex-row" :
              "right-4 bottom-4 flex-row-reverse"
            }`}
          >
            {items.map((s, idx) => (
              <div
                key={s.id || `${pos}-${idx}`}
                data-testid={`qr-overlay-slot-${s.kind || "payment"}`}
                className="bg-white rounded-xl shadow-2xl p-2 pointer-events-auto"
                style={{ width: 110 }}
              >
                <img src={s.image_url} alt={s.label || "QR code"} className="w-full h-auto block" />
                {s.label && (
                  <div className="mt-1 text-center text-[10px] font-black uppercase tracking-wider text-black/85 truncate">
                    {s.label}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : null
      ))}
    </div>
  );
}
